import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import type { QuizSession, SubscriptionPlan } from '../types'
import { fetchQuizSessionById } from '../services/quizService'

type Props = {
  userId: string
  plan: SubscriptionPlan
}

const categoryColor: Record<string, string> = {
  technical: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  conceptual: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  behavioral: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
}

const difficultyLabel: Record<string, string> = {
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
}

export function QuizResultPage({ userId }: Props) {
  const { id } = useParams<{ id: string }>()
  const [session, setSession] = useState<QuizSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id || !userId) return
    setIsLoading(true)
    fetchQuizSessionById(id, userId)
      .then(data => {
        setSession(data)
        if (!data) setError('Quiz not found')
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load quiz'))
      .finally(() => setIsLoading(false))
  }, [id, userId])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="h-8 w-48 bg-app-subtle rounded animate-pulse" />
        <div className="card p-6 space-y-4">
          <div className="h-24 bg-app-subtle rounded animate-pulse" />
          <div className="h-40 bg-app-subtle rounded animate-pulse" />
        </div>
      </div>
    )
  }

  if (error || !session) {
    return (
      <div className="mx-auto max-w-6xl">
        <div className="card p-6 text-center">
          <p className="text-rose-700 dark:text-rose-400 mb-4">{error || 'Quiz not found'}</p>
          <Link to="/quiz" className="text-sm text-app-accent hover:text-app-accent-hover">
            Back to Quizzes
          </Link>
        </div>
      </div>
    )
  }

  // Category breakdown
  const categoryBreakdown: Record<string, { correct: number; total: number }> = {}
  for (const q of session.questions) {
    if (!categoryBreakdown[q.category]) {
      categoryBreakdown[q.category] = { correct: 0, total: 0 }
    }
    categoryBreakdown[q.category].total++
    if (q.is_correct) categoryBreakdown[q.category].correct++
  }

  const scoreColor =
    session.score >= 80 ? 'text-emerald-500' :
    session.score >= 50 ? 'text-amber-500' :
    'text-rose-500'

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/quiz"
          className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Quizzes
        </Link>
      </div>

      {/* Score card */}
      <div className="card p-6 text-center space-y-4">
        <h1 className="text-2xl font-bold text-app-ink font-display">Quiz Results</h1>

        <div className={`text-6xl font-bold ${scoreColor}`}>
          {session.score}%
        </div>

        <p className="text-lg text-app-muted">
          {session.correct_answers} / {session.total_questions} correct
        </p>

        <div className="flex justify-center gap-4 text-sm text-app-muted">
          <span>Difficulty: <span className="text-app-ink font-medium">{difficultyLabel[session.difficulty]}</span></span>
          <span>Questions: <span className="text-app-ink font-medium">{session.total_questions}</span></span>
        </div>

        {/* Category breakdown */}
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          {Object.entries(categoryBreakdown).map(([cat, { correct, total }]) => (
            <div
              key={cat}
              className="flex items-center gap-2 rounded-lg border border-app-line px-3 py-1.5"
            >
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[cat] || ''}`}>
                {cat}
              </span>
              <span className="text-sm text-app-ink font-medium">
                {correct}/{total}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Questions review */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-app-ink font-display">Review Answers</h2>

        {session.questions.map((q, idx) => {
          const wasAnswered = q.user_answer !== null
          return (
            <div key={q.id} className="card p-5 space-y-3">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-0.5">
                  {q.is_correct ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-rose-500" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-app-muted font-medium">Q{idx + 1}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[q.category] || ''}`}>
                      {q.category}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-app-ink leading-relaxed">{q.text}</p>
                </div>
              </div>

              <div className="space-y-1.5 pl-8">
                {q.options.map((option, optIdx) => {
                  const isCorrect = optIdx === q.correct_option
                  const isUserAnswer = wasAnswered && optIdx === q.user_answer
                  const isWrongAnswer = isUserAnswer && !isCorrect

                  let optionClass = 'border-app-line text-app-muted'
                  if (isCorrect) {
                    optionClass = 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300'
                  }
                  if (isWrongAnswer) {
                    optionClass = 'border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300'
                  }

                  return (
                    <div
                      key={optIdx}
                      className={`rounded-lg border px-3 py-2 text-sm ${optionClass}`}
                    >
                      {option}
                      {isCorrect && <span className="ml-2 text-xs font-medium">(correct)</span>}
                      {isWrongAnswer && <span className="ml-2 text-xs font-medium">(your answer)</span>}
                    </div>
                  )
                })}
              </div>

              {q.explanation && (
                <div className="pl-8 pt-1">
                  <p className="text-sm text-app-muted bg-app-subtle rounded-lg px-3 py-2 border border-app-line">
                    <span className="font-medium text-app-ink">Explanation: </span>
                    {q.explanation}
                  </p>
                </div>
              )}

              {!wasAnswered && (
                <p className="text-xs text-app-muted pl-8 italic">Not answered</p>
              )}
            </div>
          )
        })}
      </div>

      {/* Bottom actions */}
      <div className="flex justify-center gap-3 pb-8">
        <Link
          to="/quiz"
          className="rounded-lg border border-app-line px-5 py-2.5 text-sm font-medium text-app-ink hover:bg-app-subtle transition-colors"
        >
          Back to Quizzes
        </Link>
        <Link
          to="/quiz"
          className="rounded-lg bg-app-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-app-primary-hover transition-colors"
        >
          Try Another Quiz
        </Link>
      </div>
    </div>
  )
}
